import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Modal, Spinner } from "react-bootstrap";
import "./SolicitudDetailsModal.css";
import SolicitudComentarios from "./SolicitudComentarios";
import SolicitudDecisionActions from "./SolicitudDecisionActions";

function getApiBaseUrl() {
  return process.env.REACT_APP_BASE_API || "/";
}

function getApiUrl(path) {
  const baseUrl = getApiBaseUrl();
  const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;

  return `${cleanBaseUrl}${cleanPath}`;
}

function getValue(value) {
  return value || "—";
}

function InfoItem({ label, value }) {
  return (
    <div className="solicitudes-info-item">
      <span className="solicitudes-info-label">{label}</span>
      <span className="solicitudes-info-value">{getValue(value)}</span>
    </div>
  );
}

function SolicitudDetailsModal({ show,
  rut,
  onHide,
  onComentarioSaved,
  onAccepted,
  onDiscarded, }) {
  const [solicitud, setSolicitud] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!show || !rut) return;

    const fetchSolicitudDetails = async () => {
      setIsLoading(true);
      setFeedback(null);
      setSolicitud(null);

      try {
        const response = await fetch(
          getApiUrl(`api/formularios/comunidades/${rut}/detalle/`),
          {
            credentials: "include",
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || "No se pudo cargar el detalle.");
        }

        setSolicitud(data);
      } catch (error) {
        setFeedback({
          type: "danger",
          message: error.message || "No se pudo cargar el detalle.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSolicitudDetails();
  }, [show, rut]);

  const avanzada = solicitud?.avanzada;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Detalle de solicitud</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading && (
          <div className="solicitudes-modal-loading">
            <Spinner animation="border" size="sm" />
            <span>Cargando detalle...</span>
          </div>
        )}

        {feedback && <Alert variant={feedback.type}>{feedback.message}</Alert>}

        {!isLoading && solicitud && (
          <>
            <div className="solicitudes-modal-profile-header">
              <div className="solicitudes-modal-avatar">
                {avanzada?.imagen ? (
                  <img src={avanzada.imagen} alt={solicitud.nombre || "Perfil"} />
                ) : (
                  <span>{(solicitud.nombre || "RM").slice(0, 2).toUpperCase()}</span>
                )}
              </div>

              <div className="solicitudes-modal-profile-info">
                <h5>{getValue(solicitud.nombre)}</h5>
                <p>{getValue(solicitud.rut)}</p>

                <div className="solicitudes-profile-badges">
                  <Badge bg="primary">
                    {solicitud.comuna_label || solicitud.comuna || "—"}
                  </Badge>
                </div>
              </div>

              <div className="solicitudes-modal-status">
                {solicitud.avanzada_respondida ? (
                  <Badge bg="success">
                    Lista para entrevista
                  </Badge>
                ) : (
                  <Badge bg="warning">Pendiente formulario avanzado</Badge>
                )}
              </div>
            </div>

            <SolicitudComentarios
              rut={solicitud.rut}
              initialValue={solicitud.estado}
              onSaved={(nuevoEstado) => {
                setSolicitud((currentSolicitud) => ({
                  ...currentSolicitud,
                  estado: nuevoEstado,
                }));

                if (onComentarioSaved) {
                  onComentarioSaved(solicitud.rut, nuevoEstado);
                }
              }}
            />

            <div className="solicitudes-modal-section">
              <h6>Formulario básico</h6>

              <div className="solicitudes-info-grid">
                <InfoItem label="Correo" value={solicitud.correo} />
                <InfoItem label="Celular" value={solicitud.celular} />
                <InfoItem label="Región" value={solicitud.region} />
                <InfoItem label="Comuna" value={solicitud.comuna_label} />
                <InfoItem label="Nacimiento" value={solicitud.nacimiento} />
                <InfoItem label="Nacionalidad" value={solicitud.nacionalidad_label} />
                <InfoItem label="Estado civil" value={solicitud.e_civil_label} />
                <InfoItem label="Estudios" value={solicitud.estudios_label} />
                <InfoItem label="Situación laboral" value={solicitud.s_laboral_label} />
                <InfoItem label="Oficio" value={solicitud.oficio_label} />
              </div>

            </div>

            {!avanzada && (
              <Alert variant="light" className="solicitudes-empty-advanced">
                Esta persona todavía no responde el formulario avanzado.
              </Alert>
            )}

            {avanzada && (
              <div className="solicitudes-modal-section">
                <h6>Formulario avanzado</h6>

                <div className="solicitudes-info-grid">
                  <InfoItem label="Dirección" value={avanzada.direccion} />
                  <InfoItem label="¿Puede trabajar en empresa?" value={avanzada.disponibilidad_label} />
                  <InfoItem label="¿Dónde puede trabajar?" value={avanzada.territorio_label} />
                  <InfoItem label="Licencia conducir" value={avanzada.l_conducir_label} />
                  <InfoItem label="Movilización propia" value={avanzada.m_propia_label} />
                  <InfoItem label="¿Autoriza compartir datos?" value={avanzada.b_trabajo_label} />
                  <InfoItem label="¿Cuáles son sus ingresos?" value={avanzada.ingresos_label} />
                  <InfoItem label="¿Sabe acceder a su Clave Única?" value={avanzada.c_unica_label} />
                </div>

                <div className="solicitudes-modal-subsection">
                  <span className="solicitudes-info-label">Certificaciones SEC</span>

                  {avanzada.certificaciones_sec_labels?.length > 0 ? (
                    <div className="solicitudes-badge-list">
                      {avanzada.certificaciones_sec_labels.map((certificacion) => (
                        <Badge key={certificacion} bg="success">
                          {certificacion}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="solicitudes-muted-text">
                      Sin certificaciones declaradas.
                    </p>
                  )}
                </div>

                <div className="solicitudes-modal-subsection">
                  <span className="solicitudes-info-label">Historia / experiencia</span>
                  <p className="solicitudes-long-text">{getValue(avanzada.bios)}</p>
                </div>

                <div className="solicitudes-modal-files">
                  <h6>Documentos Adjuntos</h6>

                  {avanzada.certificaciones?.length > 0 ? (
                    avanzada.certificaciones.map((documento, index) => (
                      <a
                        key={`${documento.nombre}-${index}`}
                        href={documento.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-dark"
                      >
                        {documento.nombre || `Certificación ${index + 1}`}
                      </a>
                    ))
                  ) : (
                    <p className="solicitudes-muted-text">
                      Sin documentos adjuntos.
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="solicitudes-modal-footer">
  {solicitud && (
    <SolicitudDecisionActions
      rut={solicitud.rut}
      estado={solicitud.estado}
      onAccepted={onAccepted}
      onDiscarded={onDiscarded}
    />
  )}

  <Button variant="secondary" onClick={onHide}>
    Cerrar
  </Button>
</Modal.Footer>
    </Modal>
  );
}

export default SolicitudDetailsModal;